export interface FriendRow {
  id: string;
  name: string;
  avatar: string | null;
  online: boolean;
  h2h: { gamesPlayed: number; wins: number; losses: number; draws: number };
}

export interface FriendRequestRow {
  id: string;
  createdAt: string;
  from?: { id: string; name: string; avatar: string | null };
  to?: { id: string; name: string; avatar: string | null };
}

export interface MeStats {
  id: string;
  name: string;
  avatar: string | null;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  currentWinStreak: number;
  longestWinStreak: number;
  winRate: number;
  recent: Array<{ gameId: string; finishOrder: number; isDurak: boolean; startedAt: string; endedAt: string | null }>;
}

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init, headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err?.error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export const api = {
  me: () => jfetch<{ id: string; name: string; avatar: string | null; isGuest: boolean }>('/auth/me'),
  providers: () => jfetch<{ google: boolean; facebook: boolean }>('/auth/providers'),
  guestLogin: (name: string) =>
    jfetch<{ id: string; name: string; avatar: string | null; isGuest: boolean }>('/auth/guest', { method: 'POST', body: JSON.stringify({ name }) }),
  logout: () => jfetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
  myStats: () => jfetch<MeStats>('/api/users/me/stats'),
  deleteAccount: () => jfetch<{ ok: true }>('/api/users/me', { method: 'DELETE' }),
  leaderboard: () => jfetch<Array<{ id: string; name: string; avatar: string | null; wins: number; losses: number; draws: number; gamesPlayed: number; winRate: number }>>('/api/users/leaderboard'),
  searchUsers: (q: string) => jfetch<Array<{ id: string; name: string; avatar: string | null }>>(`/api/users/search?q=${encodeURIComponent(q)}`),
  userStats: (id: string) => jfetch<MeStats & { id: string; name: string; avatar: string | null; isGuest: boolean; h2h: { gamesPlayed: number; wins: number; losses: number; draws: number } | null }>(`/api/users/${encodeURIComponent(id)}/stats`),
  // Friends
  friends: () => jfetch<Array<FriendRow>>('/api/friends'),
  friendRequests: () => jfetch<{ incoming: FriendRequestRow[]; outgoing: FriendRequestRow[] }>('/api/friends/requests'),
  sendFriendRequest: (toId: string) => jfetch<{ ok: true; status: 'PENDING' | 'ACCEPTED'; requestId: string }>('/api/friends/request', { method: 'POST', body: JSON.stringify({ toId }) }),
  respondFriendRequest: (requestId: string, accept: boolean) => jfetch<{ ok: true; status: string }>('/api/friends/respond', { method: 'POST', body: JSON.stringify({ requestId, accept }) }),
  cancelFriendRequest: (requestId: string) => jfetch<{ ok: true }>('/api/friends/cancel', { method: 'POST', body: JSON.stringify({ requestId }) }),
  removeFriend: (userId: string) => jfetch<{ ok: true }>(`/api/friends/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  gameByCode: (code: string) => jfetch<{ id: string; code: string; phase: string; hostName: string; playerCount: number; maxPlayers: number }>(`/api/games/${encodeURIComponent(code)}`),
  myGame: () => jfetch<{ gameId: string; code: string; phase: 'lobby' | 'playing'; playerCount: number; maxPlayers: number } | null>('/api/games/mine'),
  pushKey: () => jfetch<{ publicKey: string; enabled: boolean }>('/api/push/key'),
  pushSubscribe: (sub: PushSubscriptionJSON) => jfetch<{ ok: true }>('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string) => jfetch<{ ok: true }>('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
};
